import { useState, useEffect } from "react";
import { FaTimes, FaUser, FaFileAlt, FaShieldAlt, FaQrcode, FaDownload, FaExternalLinkAlt } from "react-icons/fa";
import toast from "react-hot-toast";
import {certificatesValidationApi} from '../../api/certificatesValidation.api'
function buildToken(folio, tipo) {
    return btoa(folio + "|" + tipo).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
//CertificateValidationModal

function getValidationUrl(certificate) {
    if (!certificate || !certificate.folio) return "";

    const tipo = (certificate.certificateType || "").toLowerCase();
    const token = buildToken(certificate.folio, tipo);

    const envUrl = process.env.REACT_APP_QR_VALIDATION_URL ||
                   process.env.REACT_APP_VALIDATION_URL ||
                   (typeof window !== "undefined" && window.ENV?.QR_VALIDATION_URL);

    if (envUrl && typeof envUrl === "string" && envUrl.trim()) {
        const base = envUrl.trim();
        if (base.endsWith("=") || base.endsWith("?")) {
            return `${base}${token}`;
        }
        if (base.includes("?")) {
            return `${base}&t=${token}`;
        }
        return `${base.replace(/\/$/, "")}/validate?t=${token}`;
    }

    if (typeof window !== "undefined" && window.location && window.location.origin) {
        return `${window.location.origin}/validate?t=${token}`;
    }

    return `/validate?t=${token}`;
}

function CertificatesValidationModal({ open, onClose, certificate }) {
    const [qrImageBase64, setQrImageBase64] = useState("");
    const [loadingQr, setLoadingQr] = useState(false);

    const validationUrl = getValidationUrl(certificate);

    useEffect(() => {
        if (!open || !certificate) return;

        const fetchQrCode = async () => {
            setLoadingQr(true);
            try {
                const data = await certificatesValidationApi.generateQr(validationUrl);
                setQrImageBase64(data.qr);
            } catch (err) {
                console.error("Error al obtener el QR:", err);
            } finally {
                setLoadingQr(false);
            }
        };

        fetchQrCode();
    }, [open, certificate, validationUrl]);

    if (!open || !certificate) return null;

    const handleDownload = async () => {
        try {
            const blob = await certificatesValidationApi.downloadPdf(certificate.folio);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Certificado_${(certificate.certificateType || "CERTIFICADO").toUpperCase()}_${certificate.folio}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Error al descargar el certificado.");
        }
    };

    const fechaFormateada = certificate.fechaEmision
        ? new Date(certificate.fechaEmision).toLocaleDateString("es-MX", { dateStyle: "long" })
        : "-";
    const certificateStatus = typeof certificate.status === "string" && certificate.status.trim()
        ? certificate.status
        : "-";
    const certificateStatusClass = certificateStatus === "-"
        ? "bg-gray-100 text-gray-600"
        : certificateStatus.toLowerCase() === "vigente"
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800";

    return (
        <div className="modal-overlay">
            <div className="modal-content w-full max-w-4xl flex flex-col overflow-hidden max-h-[90vh]">
                <div className="modal-header">
                    <div>
                        <h2 className="text-xl font-bold m-0">Consulta del Certificado</h2>
                        <span className="text-sm opacity-90 font-normal">Información general del certificado</span>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>
                
                <div className="p-6 overflow-y-auto bg-gray-50 flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Info General */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                            <h3 className="flex items-center gap-2 text-primary font-bold mb-4 border-b border-gray-100 pb-2"><FaFileAlt /> Información General</h3>
                            <div className="flex justify-between items-center py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500">Folio</span>
                                <strong className="text-gray-800">{certificate.folio}</strong>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500">Tipo</span>
                                <strong className="capitalize text-gray-800">{certificate.certificateType}</strong>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500">Estado</span>
                                <span
                                    role="status"
                                    aria-label="Estado del certificado"
                                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${certificateStatusClass}`}
                                >
                                    {certificateStatus}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2 text-sm">
                                <span className="text-gray-500">Fecha de Emisión</span>
                                <strong className="text-gray-800">{fechaFormateada}</strong>
                            </div>
                        </div>

                        {/* Titular */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                            <h3 className="flex items-center gap-2 text-primary font-bold mb-4 border-b border-gray-100 pb-2"><FaUser /> Titular</h3>
                            <div className="flex flex-col py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500 mb-1">Nombre</span>
                                <strong className="text-gray-800">{certificate.holder}</strong>
                            </div>
                            <div className="flex flex-col py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500 mb-1">CURP / Clave</span>
                                <strong className="text-gray-800">{certificate.curp}</strong>
                            </div>
                            <div className="flex flex-col py-2 text-sm">
                                <span className="text-gray-500 mb-1">RFC</span>
                                <strong className="text-gray-800">{certificate.rfc}</strong>
                            </div>
                        </div>

                        {/* Seguridad */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 md:col-span-2">
                            <h3 className="flex items-center gap-2 text-primary font-bold mb-4 border-b border-gray-100 pb-2"><FaShieldAlt /> Seguridad</h3>
                            <div className="flex flex-col py-2 border-b border-dashed border-gray-200 text-sm">
                                <span className="text-gray-500 mb-1">UUID de Seguridad</span>
                                <strong className="text-xs break-all text-gray-800 font-mono bg-gray-50 p-2 rounded border border-gray-200">{certificate.securityUuid}</strong>
                            </div>
                            <div className="flex flex-col py-2 text-sm mt-2">
                                <span className="text-gray-500 mb-1">Sello Digital</span>
                                <strong className="text-[10px] break-all text-gray-800 font-mono bg-gray-50 p-2 rounded border border-gray-200 leading-tight max-h-24 overflow-y-auto">{certificate.digitalSeal}</strong>
                            </div>
                        </div>

                        {/* QR */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 md:col-span-2 flex flex-col items-center">
                            <h3 className="flex items-center justify-center gap-2 text-primary font-bold mb-4 w-full border-b border-gray-100 pb-2"><FaQrcode /> Código QR de Validación</h3>
                            <div className="flex min-h-[150px] items-center justify-center p-2 text-center text-sm">
                                {loadingQr ? (
                                    <span className="text-gray-500">Cargando QR...</span>
                                ) : qrImageBase64 ? (
                                    <img
                                        src={qrImageBase64}
                                        alt="QR de validación"
                                        className="h-[150px] w-[150px] rounded-lg shadow-sm border border-gray-200"
                                    />
                                ) : (
                                    <span className="text-gray-400">No disponible</span>
                                )}
                            </div>
                            <a href={validationUrl} target="_blank" rel="noreferrer"
                                className="mt-4 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                <FaExternalLinkAlt /> Abrir enlace de validación
                            </a>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-4 bg-white border-t border-gray-200">
                    <button className="btn-primary !bg-gray-100 !text-gray-700 hover:!bg-gray-200 border border-gray-300" onClick={onClose}>Cerrar</button>
                    <button className="btn-primary" onClick={handleDownload}>
                        <FaDownload /> Descargar PDF
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CertificatesValidationModal;
